/**
 * Sentry error tracking configuration for frontend
 */

import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

export function initSentry() {
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';

  if (!sentryDsn) {
    console.log('Sentry DSN not configured - error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,

    // Performance Monitoring
    integrations: [
      new BrowserTracing({
        // Set sampling rate for performance monitoring
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/api\.yoursite\.com/,
        ],
      }),
    ],

    // Sample rate for transactions (performance monitoring)
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),

    // Sample rate for error events
    sampleRate: 1.0,

    // Release tracking
    release: process.env.REACT_APP_VERSION || '1.0.0',

    // Enable debug mode in development
    debug: environment === 'development',

    // Filter errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Network errors
      'NetworkError',
      'Network request failed',
      // Aborted errors
      'AbortError',
      'The user aborted a request',
    ],

    // Filter out localhost URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],

    // Before send hook to filter sensitive data
    beforeSend(event, hint) {
      // Filter sensitive data from errors
      if (event.request) {
        // Remove auth tokens from headers
        if (event.request.headers) {
          delete event.request.headers.Authorization;
          delete event.request.headers.authorization;
        }

        // Remove sensitive query parameters
        if (event.request.url) {
          try {
            const url = new URL(event.request.url);
            if (url.searchParams.has('token')) {
              url.searchParams.set('token', '[Filtered]');
            }
            if (url.searchParams.has('api_key')) {
              url.searchParams.set('api_key', '[Filtered]');
            }
            event.request.url = url.toString();
          } catch (e) {
            // Invalid URL, skip filtering
          }
        }

        // Filter POST data
        if (event.request.data) {
          const data = event.request.data;
          if (typeof data === 'object') {
            const sensitiveFields = ['password', 'token', 'apiKey', 'api_key', 'secret'];
            sensitiveFields.forEach(field => {
              if (field in data) {
                data[field] = '[Filtered]';
              }
            });
          }
        }
      }

      // Filter local storage data
      if (event.extra && event.extra.localStorage) {
        delete event.extra.localStorage;
      }

      return event;
    },

    // Before breadcrumb hook
    beforeBreadcrumb(breadcrumb, hint) {
      // Filter sensitive data from console breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.data) {
        const message = breadcrumb.data.arguments;
        if (message && typeof message === 'string') {
          // Remove API keys and tokens from logs
          breadcrumb.data.arguments = message.replace(/[a-zA-Z0-9]{32,}/g, '[Filtered]');
        }
      }

      // Filter HTTP request breadcrumbs
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        if (breadcrumb.data && breadcrumb.data.url) {
          try {
            const url = new URL(breadcrumb.data.url);
            if (url.searchParams.has('token')) {
              url.searchParams.set('token', '[Filtered]');
            }
            breadcrumb.data.url = url.toString();
          } catch (e) {
            // Invalid URL, skip filtering
          }
        }
      }

      return breadcrumb;
    },
  });

  console.log(`Sentry initialized - Environment: ${environment}`);
}

/**
 * Set user context for error tracking
 */
export function setUser(user: { id: number; email?: string; username?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id.toString(),
      email: user.email,
      username: user.username,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Set a tag for error tracking
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Set custom context
 */
export function setContext(name: string, context: Record<string, any>) {
  Sentry.setContext(name, context);
}

/**
 * Add a breadcrumb manually
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Wrap a component with Sentry error boundary
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

/**
 * Higher-order component to track component profiling
 */
export const withProfiler = Sentry.withProfiler;
